import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Mail, CheckCircle, AlertCircle } from "lucide-react";
import { submitProfileDownload } from "../lib/api";

const disposableDomains = [
  "mailinator.com", "guerrillamail.com", "temp-mail.org", "throwaway.email",
  "yopmail.com", "tempmail.com", "fakeinbox.com", "sharklasers.com",
  "guerrillamail.info", "grr.la", "tempinbox.com", "discard.email",
  "trashmail.com", "mailnesia.com", "maildrop.cc", "10minutemail.com"
];

const validateEmail = (email) => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!regex.test(email)) return "Please enter a valid email address";
  const domain = email.split("@")[1].toLowerCase();
  if (disposableDomains.includes(domain)) return "Please use a real email address (temporary inboxes are blocked)";
  const local = email.split("@")[0];
  if (local.length < 2) return "Please enter a valid email address";
  if (/^(test|abc|xxx|aaa|fake)@/i.test(email)) return "Please enter a valid email address";
  return null;
};

const ProfileDownloadModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Please enter your full name");
      return;
    }
    const emailErr = validateEmail(form.email);
    if (emailErr) { setError(emailErr); return; }

    setSubmitting(true);
    try {
      await submitProfileDownload(form);
      setSentTo(form.email);
      setSuccess(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(detail);
    }
    setSubmitting(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => { setForm({ name: "", email: "" }); setError(""); setSuccess(false); setSentTo(""); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-white border-[#E5DDD3]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#1C2B3A]">
            {success ? "Profile Sent" : "Request Profile"}
          </DialogTitle>
          <DialogDescription className="font-sans text-[#7A7A7A]">
            {success
              ? `My detailed profile has been emailed to ${sentTo}. Please check your inbox (and spam folder).`
              : "Enter your name and email — I will email my profile (PDF) to the address you provide."}
          </DialogDescription>
        </DialogHeader>
        {success ? (
          <div className="flex flex-col items-center py-6 gap-3" data-testid="download-success">
            <CheckCircle size={48} className="text-green-600" />
            <p className="font-sans text-sm text-[#4A4A4A] text-center">
              Sent to <strong>{sentTo}</strong>. If you don't see it within a few minutes, check your spam folder or try a different email.
            </p>
            <Button onClick={handleClose} data-testid="download-close-btn" className="mt-2 bg-[#1C2B3A] hover:bg-[#2A3F54] text-white font-sans text-sm h-9">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="font-sans text-sm text-[#1C2B3A]">Full Name *</Label>
              <Input
                data-testid="download-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                className="border-[#E5DDD3] focus-visible:ring-[#B8945A]/20 font-sans"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-sans text-sm text-[#1C2B3A]">Professional Email *</Label>
              <Input
                data-testid="download-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="your@organization.com"
                className="border-[#E5DDD3] focus-visible:ring-[#B8945A]/20 font-sans"
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 text-red-600 text-sm font-sans" data-testid="download-error">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="download-submit-btn"
              className="w-full bg-[#1C2B3A] hover:bg-[#2A3F54] text-white font-sans text-sm tracking-wider h-10 gap-2"
            >
              <Mail size={14} />
              {submitting ? "Sending..." : "Email Me the Profile"}
            </Button>
            <p className="text-xs text-[#7A7A7A] font-sans text-center">
              Your information is protected under our{" "}
              <a href="/privacy" className="text-[#B8945A] underline">Privacy Policy</a>.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileDownloadModal;
